import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import {
  type Chatbox,
  type CustomFieldPredicate,
  type Inbox,
  type SendMessageEvent,
  type Session,
} from 'talkjs/all';
import { Box } from '@rsos/components/capstone/base';
import { ACCEPTED } from '@rsos/constants/alertStatuses';
import { CONVERSATION_ENDED } from '@rsos/constants/chatStatuses';
import useLoggedInUser from '@rsos/hooks/useLoggedInUser';
import {
  addPrivilegedParticipants,
  resetUnreadMessages,
} from '@rsos/reduxDataStore/src/features/alerts/chatSlice';
import { useCurrentConversation } from '@rsos/reduxDataStore/src/features/alerts/hooks';
import { addFollower } from '@rsos/reduxDataStore/src/features/alerts/incidentsSlice';
import { useSelectedAlert } from '@rsos/reduxDataStore/src/features/selectors/alertsSelectors';
import { captureException } from '@rsos/utils/sentry';
import {
  ALERT_PREFIX,
  CHAT_RAPTOR_PREFIX,
  createTalkSession,
  createTalkUser,
  getChatFilterPermissions,
} from '@rsos/utils/talkJS';
import { RootState, useAppDispatch } from '../../app';
import useTalkJSTheme from '../../hooks/useTalkJSTheme';

const HiddenComponent = styled.div`
  display: none;
`;

const CHAT_PERMISSIONS = {
  'permission.csp': 'False',
  'permission.icsp': 'False',
  'permission.midas_user': 'False',
  'permission.rsp': 'True',
};

interface MessageFieldVisibilityArgs {
  alertStatus?: string;
  conversationStatus?: string;
}

/**
 * TalkJS message field visibility rules:
 * Show:
 * - alert status is ACCEPTED and conversation status is not CONVERSATION_ENDED
 * Hide:
 * - alert is supplemental only
 * - alert status is NEW, DECLINED, TIMEOUT, CANCELED, IGNORED
 * - conversation status is CONVERSATION_ENDED
 * Do not check isChatEnabled — it is not used for Portal alerts, only iCSP.
 */
export const checkMessageFieldVisibility = ({
  alertStatus,
  conversationStatus,
}: MessageFieldVisibilityArgs): boolean => {
  return (
    alertStatus === ACCEPTED &&
    !!conversationStatus &&
    conversationStatus !== CONVERSATION_ENDED
  );
};

interface TalkJSPrivilegedChatboxProps {
  isChatBoxVisible: boolean;
}

/**
 * Creates a TalkJS chat iframe container for privileged alert conversations.
 * When an alert status is accepted, the dispatcher can participate in a
 * secondary conversation with users in RSP and CSP via this component.
 * Uses both a hidden Inbox (for participant tracking) and a visible Chatbox.
 */
const TalkJSPrivilegedChatbox = ({
  isChatBoxVisible,
}: TalkJSPrivilegedChatboxProps) => {
  const dispatch = useAppDispatch();

  const chatTheme = useTalkJSTheme();

  const profile = useSelector((state: RootState) => state.sinatra.user.profile);

  const {
    account_id: currentPSAPAccountID,
    display_name: currentPSAPDisplayName,
  } = useSelector((state: RootState) => state.psaps.currentPSAP);

  const selectedAlertID = useSelector(
    (state: RootState) => state.incidents.selectedAlertID,
  );

  const chats = useSelector((state: RootState) => state.chat.chats);

  const currentAlert = useSelectedAlert();
  const alertStatus = currentAlert?.status?.name;

  const alertConversation = useCurrentConversation();
  const alertConversationID = alertConversation?.privileged_conversation?.id;
  const alertConversationStatus =
    alertConversation?.privileged_conversation?.status;

  const loggedInUser = useLoggedInUser('rsp_alerts_user');

  const [chatSession, setChatSession] = useState<Session | null>(null);
  const [inbox, setInbox] = useState<Inbox | null>(null);
  const [chatbox, setChatbox] = useState<Chatbox | null>(null);
  const [openedAlerts, setOpenedAlerts] = useState(new Set<string>());

  const chatRef = useRef<HTMLDivElement>(null);
  const inboxRef = useRef<HTMLDivElement>(null);

  /**
   * TalkJS chatbox send message handler.
   * Overrides each outgoing message with RSP-specific metadata before it is sent.
   * https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Chatbox.html#Chatbox.on__sendMessage__
   */
  const handleSendMessage = useCallback(
    (event: SendMessageEvent) => {
      const permissions = CHAT_PERMISSIONS;

      event.override({
        custom: {
          entity_display_name: currentPSAPDisplayName || '',
          entity_id: currentPSAPAccountID || '',
          from_app: 'RSP',
          sender: loggedInUser.name,
          ...(selectedAlertID && {
            selectedAlertID,
            ...permissions,
          }),
        },
        text: event.message.text,
      });

      if (selectedAlertID && profile?.id) dispatch(addFollower(profile.id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedAlertID],
  );

  // =========================================
  //  TALKJS CHATBOX & UNREADS INITIALIZATION
  // =========================================

  // Creates and mounts the TalkJS session, inbox, and chatbox when the AlertPane opens.
  // All three are destroyed on unmount (when the pane closes).
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Session/#Session__createChatbox
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Chatbox/#Chatbox__mount
  useEffect(() => {
    let session: Session | undefined,
      chat: Chatbox | undefined,
      inboxInstance: Inbox | undefined;

    try {
      (async () => {
        session = await createTalkSession(loggedInUser);
        if (!session) return;

        inboxInstance = session.createInbox();

        chat = session.createChatbox({
          theme: chatTheme,
          showChatHeader: false,
          messageField: {
            visible: false,
            placeholder: 'Type response',
          },
        });

        inboxInstance.mount(inboxRef.current);
        chat.mount(chatRef.current);

        inboxInstance.onConversationSelected(event => {
          if (event.participants) {
            const participantList = event.participants.filter(
              participant => participant.id !== event.me.id,
            );
            dispatch(addPrivilegedParticipants(participantList));
          }
        });

        setChatSession(session || null);
        setInbox(inboxInstance || null);
        setChatbox(chat || null);
      })();
    } catch (error) {
      captureException(error);
    }

    return () => {
      if (session) {
        setChatbox(null);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTheme]);

  // When minimizing the chat panel we deselect the conversation from the chatbox
  // while keeping it selected in the inbox. Deselecting the chatbox allows unread
  // message tracking to work correctly and resets on re-open.
  // TalkJS only updates the participant list when the Inbox selects a conversation,
  // so the inbox must stay selected for initial load.
  //
  // select(null) — de-selects in the UI, hiding the message list.
  // getOrCreateConversation(undefined) has no errors but TalkJS behavior is undefined,
  // so we guard with the alertConversationID check below.
  useEffect(() => {
    if (!isChatBoxVisible) {
      chatbox?.select(null);
      let conversation;

      if (alertConversationID) {
        conversation =
          chatSession?.getOrCreateConversation(alertConversationID);
      }

      if (conversation) {
        conversation.setParticipant(createTalkUser(loggedInUser));
        inbox?.select(conversation);
      } else {
        inbox?.select(null);
      }
    }
  }, [
    alertConversationID,
    chatSession,
    chatbox,
    inbox,
    isChatBoxVisible,
    loggedInUser,
  ]);

  // ========================
  //  ALERTS USEEFFECT HOOKS
  // ========================

  // When switching between alerts while the pane is open, the session and UI stay alive.
  // The conversation is swapped using select() and message field visibility is re-evaluated.
  // The selectedAlertID suffix and alertConversationID suffix must match so TalkJS selects
  // the correct alert conversation from the backend.
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Session/#Session__getOrCreateConversation
  // https://talkjs.com/docs/Reference/JavaScript_Chat_SDK/Chatbox/#Chatbox__select
  useEffect(() => {
    if (
      chatbox &&
      chatSession &&
      selectedAlertID &&
      alertConversationID &&
      selectedAlertID.split(ALERT_PREFIX)[1] ===
        alertConversationID.split(CHAT_RAPTOR_PREFIX)[1] &&
      isChatBoxVisible
    ) {
      const conversation =
        chatSession.getOrCreateConversation(alertConversationID);

      conversation.setParticipant(createTalkUser(loggedInUser));

      chatbox.select(conversation);
      inbox?.select(conversation);

      const isMessageFieldVisible = checkMessageFieldVisibility({
        alertStatus,
        conversationStatus: alertConversationStatus,
      });

      chatbox.messageField.setVisible(isMessageFieldVisible);

      const customFilter = getChatFilterPermissions() as unknown as Record<
        string,
        CustomFieldPredicate
      >;

      chatbox.setMessageFilter({ custom: { ...customFilter } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    alertConversationID,
    alertConversationStatus,
    alertStatus,
    chatbox,
    chatSession,
    selectedAlertID,
    isChatBoxVisible,
  ]);

  // onSendMessage accumulates listeners if called multiple times on the same chatbox instance.
  // Keeping it in its own effect with the openedAlerts guard ensures it is registered only once
  // per alert, preventing duplicate message overrides from stacking up.
  useEffect(() => {
    if (
      chatbox &&
      chatSession &&
      chatTheme &&
      !openedAlerts.has(selectedAlertID ?? '') &&
      selectedAlertID &&
      alertConversationID &&
      selectedAlertID.split(ALERT_PREFIX)[1] ===
        alertConversationID.split(CHAT_RAPTOR_PREFIX)[1] &&
      isChatBoxVisible
    ) {
      setOpenedAlerts(new Set(openedAlerts.add(selectedAlertID)));

      chatbox.onSendMessage(event => {
        if (alertConversationID === event.conversation.id) {
          handleSendMessage(event);
        }
      });

      chatbox.onFocus(() => {
        dispatch(resetUnreadMessages(selectedAlertID));
      });
    }
  }, [
    alertConversationID,
    chatbox,
    chats,
    chatSession,
    chatTheme,
    dispatch,
    handleSendMessage,
    isChatBoxVisible,
    openedAlerts,
    selectedAlertID,
  ]);

  // Reset the opened alerts tracker when the theme changes (e.g. user switches light/dark).
  // This ensures onSendMessage listeners are re-registered on the new chatbox instance.
  useEffect(() => {
    if (chatTheme && openedAlerts.size > 0) {
      setOpenedAlerts(new Set());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatTheme]);

  return (
    <>
      <HiddenComponent ref={inboxRef} />
      <Box width="100%" height="100%" ref={chatRef} />
    </>
  );
};

export default TalkJSPrivilegedChatbox;
